import React, { FC, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { IconShell } from 'components/atoms/icon-shell';
import { StatusIndicator } from 'components/molecules/status-indicator';
import { useProjectTemplateProjectPhase } from 'data/templates/projects';
import {
  deletePhaseInProjectTemplate,
  updatePhaseInProjectTemplate,
} from 'data/templates/projects/actions';
import { queueMacrotask } from 'utils/macrotask';
import { ProjectPhaseMoreActions } from '../../commons';
import { MoreActions, NewPhaseRow, TextWrapper, Title } from './styles';

export const RenderProjectPhaseName: FC<{
  id: number;
  onClick(): void;
  performAutoFocus(): void;
  autoFocus: boolean;
}> = ({ id, onClick, autoFocus, performAutoFocus }) => {
  const projectPhase = useProjectTemplateProjectPhase(id);
  const dispatch = useDispatch();

  const updatePhaseName = useCallback(
    (phaseName: string) => {
      dispatch(
        updatePhaseInProjectTemplate({
          projectPhaseId: projectPhase.projectPhaseId,
          payload: {
            ...projectPhase,
            projectPhaseName: phaseName,
            tasks: [],
            private: false,
          },
          handleSuccess: () => {
            queueMacrotask(() =>
              (
                document.querySelector(
                  `[data-row-key="PROJECT_PHASE-${projectPhase.projectPhaseId}"] .js__expand`,
                ) as HTMLButtonElement
              )?.click(),
            );
          },
        }),
      );
    },
    [dispatch, projectPhase],
  );

  const handlePhaseNameChange = useCallback(
    async (phaseName?: string) => {
      if (phaseName) {
        updatePhaseName(phaseName);
      } else if (projectPhase.isNew) {
        dispatch(deletePhaseInProjectTemplate(projectPhase));
      }
    },
    [dispatch, updatePhaseName, projectPhase],
  );

  return projectPhase.isNew ? (
    <NewPhaseRow
      autoFocus={autoFocus}
      clearOnBlur
      onAdd={handlePhaseNameChange}
      onBlur={handlePhaseNameChange}
      placeholder="Add new phase"
    />
  ) : (
    <>
      <TextWrapper onClick={onClick} data-cy={projectPhase?.projectPhaseName}>
        <IconShell
          icon={
            <StatusIndicator
              progress={0}
              status={{
                label: 'To do',
                group: 1,
                color: 'blue',
              }}
            />
          }
          size="xsmall"
        />
        <Title>{projectPhase?.projectPhaseName}:</Title>
      </TextWrapper>
      <MoreActions className="more__actions">
        <ProjectPhaseMoreActions
          projectPhaseId={id}
          performAutoFocus={performAutoFocus}
        />
      </MoreActions>
    </>
  );
};
